import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ProjectLaborCost, LaborType, TradeType, ProjectLaborCostCalculator } from '../../objects/project-labor-cost';
import { Project } from '../../objects/Project';
import { ProjectLaborService } from '../../Services/project-labor.service';
import { ContractorService } from '../../Services/contractor.service';
import { AuthService } from '../../components/services/auth.service';
import { Contractor } from '../../objects/Contractor';

@Component({
  selector: 'app-project-labor',
  templateUrl: './project-labor.component.html',
  styleUrls: ['./project-labor.component.css']
})
export class ProjectLaborComponent implements OnInit, OnChanges, OnDestroy {
  @Input() project?: Project;
  @Output() paidStatusChanged = new EventEmitter<void>();

  laborForm: FormGroup;
  laborEntries: ProjectLaborCost[] = [];
  dataSource = new MatTableDataSource<ProjectLaborCost>([]);
  editingEntry: ProjectLaborCost | null = null;
  isLoading = false;
  showAddForm = false;
  subContractors: Contractor[] = [];
  filteredContractors: Contractor[] = [];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Enum arrays for dropdowns
  laborTypes = Object.values(LaborType);
  tradeTypes = Object.values(TradeType);

  // Table columns
  displayedColumns: string[] = [
    'workDate',
    'workerName',
    'trade',
    'laborType',
    'regularHours',
    'overtimeHours',
    'totalCost',
    'billedAmount',
    'grossProfit',
    'isPaid',
    'actions'
  ];

  constructor(
    private snackBar: MatSnackBar,
    private laborService: ProjectLaborService,
    private contractorService: ContractorService,
    private authService: AuthService
  ) {
    this.laborForm = this.createLaborForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project?.id) {
      this.loadLaborEntries();
    }
  }

  ngOnInit(): void {
    this.loadLaborEntries();
    this.loadSubContractors();

    const laborSub = this.laborService.laborEntries$.subscribe(entries => {
      this.laborEntries = entries;
      this.dataSource.data = entries;
    });
    this.subscriptions.push(laborSub);

    const nameSub = this.laborForm.get('workerName')!.valueChanges.subscribe(value => {
      const term = (value || '').toLowerCase();
      this.filteredContractors = term
        ? this.subContractors.filter(c => c.name.toLowerCase().includes(term))
        : this.subContractors.slice();
    });
    this.subscriptions.push(nameSub);
  }

  loadSubContractors(): void {
    const user = this.authService.getCurrentUser();
    if (!user) { return; }
    const sub = this.contractorService.getContractorsByProvider(user.id).subscribe({
      next: (contractors) => {
        this.subContractors = contractors || [];
        this.filteredContractors = this.subContractors.slice();
      },
      error: () => {
        this.subContractors = [];
        this.filteredContractors = [];
      }
    });
    this.subscriptions.push(sub);
  }

  onContractorSelect(name: string): void {
    const contractor = this.subContractors.find(c => c.name === name);
    if (!contractor) { return; }
    this.laborForm.patchValue({
      subcontractorId: contractor.id,
      regularRate: contractor.hourlyWage || 0
    });
    if (contractor.hourlyWage) { this.onRegularRateChange(); }
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  createLaborForm(): FormGroup {
    return new FormGroup({
      id: new FormControl(''),
      projectId: new FormControl(0),
      employeeId: new FormControl(null),
      subcontractorId: new FormControl(null),
      workerName: new FormControl('', [Validators.required]),
      laborType: new FormControl(LaborType.HourlyEmployee, [Validators.required]),
      trade: new FormControl(TradeType.GeneralLabor, [Validators.required]),
      workDate: new FormControl(new Date(), [Validators.required]),
      startTime: new FormControl(null),
      endTime: new FormControl(null),
      regularHours: new FormControl(0, [Validators.min(0), Validators.max(24)]),
      overtimeHours: new FormControl(0, [Validators.min(0), Validators.max(24)]),
      doubleTimeHours: new FormControl(0, [Validators.min(0), Validators.max(24)]),
      regularRate: new FormControl(0, [Validators.min(0)]),
      overtimeRate: new FormControl(0, [Validators.min(0)]),
      doubleTimeRate: new FormControl(0, [Validators.min(0)]),
      isFlatRate: new FormControl(false),
      flatRateAmount: new FormControl(null, [Validators.min(0)]),
      flatBillAmount: new FormControl(null, [Validators.min(0)]),
      burdenPercentage: new FormControl(35, [Validators.min(0), Validators.max(100)]),
      billingRate: new FormControl(0, [Validators.min(0)]),
      description: new FormControl(''),
      phaseOrTask: new FormControl(''),
      isPrevailingWageJob: new FormControl(false),
      unionClassification: new FormControl(''),
      isPaid: new FormControl(false),
      paidDate: new FormControl(null)
    });
  }

  loadLaborEntries(): void {
    if (this.project?.id) {
      this.isLoading = true;
      const loadSub = this.laborService.getLaborEntriesByProject(this.project.id)
        .subscribe({
          next: (entries) => {
            this.laborEntries = entries;
            this.dataSource.data = entries;
            this.isLoading = false;
          },
          error: () => {
            this.laborEntries = [];
            this.dataSource.data = [];
            this.isLoading = false;
          }
        });
      this.subscriptions.push(loadSub);
    } else {
      this.laborEntries = [];
      this.dataSource.data = [];
      this.isLoading = false;
    }
  }

  onSubmit(): void {
    if (this.laborForm.valid) {
      const formValue = { ...this.laborForm.value };
      
      // Set project ID from input
      formValue.projectId = this.project?.id || 0;
      
      // Generate ID if creating new entry
      if (!formValue.id) {
        formValue.id = this.generateGuid();
        formValue.createdAt = new Date();
      } else {
        formValue.updatedAt = new Date();
      }

      // Calculate computed fields
      const laborCost = ProjectLaborCostCalculator.updateCalculatedFields(formValue);

      if (this.editingEntry) {
        this.updateLaborEntry(laborCost);
      } else {
        this.addLaborEntry(laborCost);
      }

      this.resetForm();
    } else {
      this.markFormGroupTouched();
    }
  }

  addLaborEntry(laborCost: ProjectLaborCost): void {
    const addSub = this.laborService.create(laborCost).subscribe({
      next: (newEntry) => {
        this.showSuccessMessage('Labor entry added successfully!');
        this.showAddForm = false; // Hide form after successful add
      },
      error: (error) => {
        console.error('Error adding labor entry:', error);
        this.showErrorMessage('Failed to add labor entry');
        
        // Fallback to MVC pattern if REST fails
        console.log('Trying MVC pattern...');
        this.addLaborEntryMvc(laborCost);
      }
    });
    this.subscriptions.push(addSub);
  }

  private addLaborEntryMvc(laborCost: ProjectLaborCost): void {
    const addSub = this.laborService.createMvc(laborCost).subscribe({
      next: (newEntry) => {
        this.showSuccessMessage('Labor entry added successfully!');
        this.showAddForm = false;
      },
      error: (error) => {
        console.error('Error adding labor entry (MVC):', error);
        this.showErrorMessage('Failed to add labor entry');
      }
    });
    this.subscriptions.push(addSub);
  }

  updateLaborEntry(laborCost: ProjectLaborCost): void {
    const updateSub = this.laborService.update(laborCost.id!, laborCost).subscribe({
      next: (updatedEntry) => {
        this.showSuccessMessage('Labor entry updated successfully!');
        this.showAddForm = false; // Hide form after successful update
      },
      error: (error) => {
        console.error('Error updating labor entry:', error);
        this.showErrorMessage('Failed to update labor entry');
        
        // Fallback to MVC pattern if REST fails
        console.log('Trying MVC pattern for update...');
        this.updateLaborEntryMvc(laborCost);
      }
    });
    this.subscriptions.push(updateSub);
  }

  private updateLaborEntryMvc(laborCost: ProjectLaborCost): void {
    const updateSub = this.laborService.editMvc(laborCost.id!, laborCost).subscribe({
      next: (updatedEntry) => {
        this.showSuccessMessage('Labor entry updated successfully!');
        this.showAddForm = false;
      },
      error: (error) => {
        console.error('Error updating labor entry (MVC):', error);
        this.showErrorMessage('Failed to update labor entry');
      }
    });
    this.subscriptions.push(updateSub);
  }

  editEntry(entry: ProjectLaborCost): void {
    this.editingEntry = entry;
    this.showAddForm = true; // Show form when editing
    this.laborForm.patchValue(entry);
  }

  showAddLaborForm(): void {
    this.showAddForm = true;
    this.editingEntry = null;
    this.resetForm();
  }

  cancelAddForm(): void {
    this.showAddForm = false;
    this.editingEntry = null;
    this.resetForm();
  }

  deleteEntry(entry: ProjectLaborCost): void {
    if (!entry.id) {
      this.showErrorMessage('Cannot delete: entry has no ID');
      return;
    }
    if (!confirm('Are you sure you want to delete this labor entry?')) { return; }

    this.isLoading = true;
    const deleteSub = this.laborService.deleteById(entry.id).subscribe({
      next: () => {
        // DELETE returns 204 No Content (null) on success
        this.showSuccessMessage('Labor entry deleted successfully!');
        this.loadLaborEntries();
      },
      error: () => {
        this.showErrorMessage('Failed to delete labor entry');
        this.isLoading = false;
      }
    });
    this.subscriptions.push(deleteSub);
  }

  togglePaid(entry: ProjectLaborCost): void {
    const updated = { ...entry, isPaid: !entry.isPaid, paidDate: !entry.isPaid ? new Date() : undefined };
    const sub = this.laborService.update(entry.id!, updated).subscribe({
      next: () => {
        this.showSuccessMessage(updated.isPaid ? 'Marked as paid' : 'Marked as unpaid');
        this.paidStatusChanged.emit();
      },
      error: () => this.showErrorMessage('Failed to update payment status')
    });
    this.subscriptions.push(sub);
  }

  resetForm(): void {
    this.editingEntry = null;
    this.laborForm.reset();
    this.laborForm.patchValue({
      laborType: LaborType.HourlyEmployee,
      trade: TradeType.GeneralLabor,
      workDate: new Date(),
      burdenPercentage: 35,
      isFlatRate: false,
      flatRateAmount: null,
      flatBillAmount: null,
      isPrevailingWageJob: false
    });
  }

  // Calculate overtime rates automatically
  onRegularRateChange(): void {
    const regularRate = this.laborForm.get('regularRate')?.value || 0;
    this.laborForm.patchValue({
      overtimeRate: regularRate * 1.5,
      doubleTimeRate: regularRate * 2.0
    });
  }

  // Real-time calculation display
  getCalculatedValues(): any {
    const formValue = this.laborForm.value;
    const basePay = ProjectLaborCostCalculator.calculateBasePay(formValue);
    const burdenAmount = formValue.isFlatRate
      ? 0
      : ProjectLaborCostCalculator.calculateBurdenAmount(basePay, formValue.burdenPercentage || 35);
    const totalCost = ProjectLaborCostCalculator.calculateTotalCost(basePay, burdenAmount);
    const billedAmount = ProjectLaborCostCalculator.calculateBilledAmount(formValue);
    const grossProfit = ProjectLaborCostCalculator.calculateGrossProfit(billedAmount, totalCost);

    return {
      basePay,
      burdenAmount,
      totalCost,
      billedAmount,
      grossProfit
    };
  }

  getTotalProjectLabor(): any {
    const totals = this.laborEntries.reduce((acc, entry) => {
      acc.totalCost += entry.totalCost;
      acc.billedAmount += entry.billedAmount;
      acc.grossProfit += entry.grossProfit;
      acc.totalHours += entry.regularHours + entry.overtimeHours + entry.doubleTimeHours;
      return acc;
    }, { totalCost: 0, billedAmount: 0, grossProfit: 0, totalHours: 0 });

    return totals;
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.laborForm.controls).forEach(key => {
      this.laborForm.get(key)?.markAsTouched();
    });
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }
}
